import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Stack,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  Tooltip,
} from '@mui/material';
import parse from 'html-react-parser';
import { Check, Close } from '@mui/icons-material';
import { translate as t } from 'hooks/useTranslations';
import Modal from 'components/_layouts/Modal';
import { success, error, warning, neutral } from '_lex/_foundations/phxColors';
import { AppContext } from 'views/App/context';

import type { DTB, VideoContentBlock } from '../../_types/dtb';
import useDTB from '../_data_sources/useDTB';
import BlockPreview from './components/BlockPreview';
import PolicyReferencePreview from './components/PolicyReferencePreview';
import {
  extractUniqueTextEntitiesFromGIQ,
  processCustomTextEntities,
  processLexientityContent,
  combineTextEntities,
} from '../../utils/textEntityProcessor';
import useDraftManualGIQs from '../../../Policy/DraftManuals/_data_sources/giq/useDraftManualGIQs';
import { getCustomTextEntities } from '../../../Policy/DraftManuals/_data_sources/customTextEntities/useCustomTextEntities';
import { getDraftManual } from '../../../Policy/DraftManuals/_data_sources/useDraftManual';

interface DTBPreviewProps {
  dtbId: string | null;
  dtbName?: string;
  open: boolean;
  onClose: () => void;
}

type PreviewState = 'content' | 'quiz' | 'results';

const DTBPreview: React.FC<DTBPreviewProps> = ({ dtbId, dtbName, open, onClose }) => {
  const { currentOrganizationId } = useContext(AppContext);
  const [currentState, setCurrentState] = useState<PreviewState>('content');
  const [selectedAnswer, setSelectedAnswer] = useState<string>('');
  const [submittedAnswer, setSubmittedAnswer] = useState<string>('');
  const [textEntities, setTextEntities] = useState<any[]>([]);
  const [isLoadingTextEntities, setIsLoadingTextEntities] = useState(false);

  const dtbQuery = useDTB(currentOrganizationId || '', dtbId || '');
  const dtb = dtbQuery.data;
  const isLoading = 'isFetching' in dtbQuery ? dtbQuery.isFetching : false;
  const isError = 'isError' in dtbQuery ? dtbQuery.isError : false;

  // Get GIQ data from the manual linked to the DTB
  const giqQuery = useDraftManualGIQs(dtb?.manual?.id || '');

  // Fetch and process text entities when DTB and GIQ data are available
  useEffect(() => {
    const fetchTextEntities = async () => {
      if (!dtb?.manual?.id) {
        setTextEntities([]);
        return;
      }

      setIsLoadingTextEntities(true);
      try {
        // Get GIQ text entities
        const giqTextEntities = giqQuery.data?.questions
          ? extractUniqueTextEntitiesFromGIQ(giqQuery.data.questions)
          : [];

        // Get draft manual to check if it's AGENCY level for custom text entities
        const draftManual = await getDraftManual(dtb.manual.id);

        let customTextEntities: any[] = [];
        if (draftManual?.manualLevel === 'AGENCY') {
          try {
            const customTextEntitiesResponse = await getCustomTextEntities(dtb.manual.id);
            customTextEntities = processCustomTextEntities(customTextEntitiesResponse.content);
          } catch (error) {
            console.error('Failed to fetch custom text entities:', error);
          }
        }

        // Combine both types of text entities
        const allTextEntities = combineTextEntities(giqTextEntities, customTextEntities);
        setTextEntities(allTextEntities);
      } catch (error) {
        console.error('Failed to fetch text entities:', error);
        setTextEntities([]);
      } finally {
        setIsLoadingTextEntities(false);
      }
    };

    fetchTextEntities();
  }, [dtb?.manual?.id, giqQuery.data]);

  // Process content blocks to resolve lexientity elements
  const processedContentBlocks = useMemo(() => {
    if (!dtb?.contentBlocks) {
      return [];
    }

    // If we're still loading text entities OR GIQ data is being refetched, return null to show loading state
    if (isLoadingTextEntities || giqQuery.isFetching) {
      return null;
    }

    // Process content with text entities (even if empty array)
    return dtb.contentBlocks.map((block) => {
      if (block.contentType === 'VIDEO') {
        const videoBlock = block as VideoContentBlock;
        return {
          ...videoBlock,
          content: processLexientityContent(block.content, textEntities),
          contentData: {
            ...videoBlock.contentData,
            caption: videoBlock.contentData?.caption
              ? processLexientityContent(videoBlock.contentData.caption, textEntities)
              : undefined,
          },
        };
      }
      return {
        ...block,
        content: processLexientityContent(block.content, textEntities),
      };
    });
  }, [dtb?.contentBlocks, textEntities, isLoadingTextEntities, giqQuery.isFetching]);

  // Process questions to resolve lexientity elements
  const processedQuestions = useMemo(() => {
    if (!dtb?.questions) {
      return [];
    }

    // If we're still loading text entities OR GIQ data is being refetched, return null to show loading state
    if (isLoadingTextEntities || giqQuery.isFetching) {
      return null;
    }

    // Process questions with text entities (even if empty array)
    return dtb.questions.map((question) => ({
      ...question,
      question: processLexientityContent(question.question, textEntities),
    }));
  }, [dtb?.questions, textEntities, isLoadingTextEntities, giqQuery.isFetching]);

  const handleClose = () => {
    // Reset state when closing
    setCurrentState('content');
    setSelectedAnswer('');
    setSubmittedAnswer('');
    onClose();
  };

  const handleContinueToQuiz = () => {
    setCurrentState('quiz');
  };

  const handleBackToContent = () => {
    setCurrentState('content');
    setSelectedAnswer('');
    setSubmittedAnswer('');
  };

  const handleSubmitAnswer = () => {
    setSubmittedAnswer(selectedAnswer);
    setCurrentState('results');
  };

  const handleResubmitAnswer = () => {
    setCurrentState('quiz');
    setSelectedAnswer('');
    setSubmittedAnswer('');
  };

  const handleClosePreview = () => {
    handleClose();
  };

  const modalTitle = dtb
    ? `${t('dtbs.library.detail.preview')}: ${dtb.name}`
    : dtbName
      ? `${t('dtbs.library.detail.preview')}: ${dtbName}`
      : t('common.preview');

  const renderActions = () => {
    if (currentState === 'content') {
      const hasQuestions = dtb?.questions && dtb.questions.length > 0;
      const continueButton = (
        <Button
          variant='contained'
          onClick={handleContinueToQuiz}
          disabled={!hasQuestions}
        >
          {t('dtbs.library.detail.previewActions.continueToQuiz')}
        </Button>
      );

      return hasQuestions ? (
        continueButton
      ) : (
        <Tooltip title={t('dtbs.library.detail.previewWarnings.noQuestionSet')}>
          <span>{continueButton}</span>
        </Tooltip>
      );
    }

    if (currentState === 'quiz') {
      return (
        <>
          <Button
            variant='outlined'
            onClick={handleBackToContent}
          >
            {t('dtbs.library.detail.previewActions.back')}
          </Button>
          <Button
            variant='contained'
            onClick={handleSubmitAnswer}
            disabled={!selectedAnswer}
          >
            {t('dtbs.library.detail.previewActions.submit')}
          </Button>
        </>
      );
    }

    if (currentState === 'results') {
      const isCorrect =
        dtb?.questions && dtb.questions.length > 0 && submittedAnswer === dtb.questions[0].correctChoiceId;

      return (
        <>
          <Button
            variant='outlined'
            onClick={handleBackToContent}
          >
            {t('dtbs.library.detail.previewActions.reviewContent')}
          </Button>
          <Button
            variant='contained'
            onClick={isCorrect ? handleClosePreview : handleResubmitAnswer}
          >
            {isCorrect
              ? t('dtbs.library.detail.previewActions.closePreview')
              : t('dtbs.library.detail.previewActions.resubmitAnswer')}
          </Button>
        </>
      );
    }

    return null;
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={modalTitle}
      size='lg'
      actions={renderActions()}
      sx={{
        '& .MuiDialogContent-root': {
          maxHeight: '80vh',
          overflowY: 'auto',
        },
      }}
    >
      <Box sx={{ p: 3, pb: 1 }}>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress />
          </Box>
        ) : dtb ? (
          <Box>
            {currentState === 'content' && (
              <Stack spacing={6}>
                {processedContentBlocks === null ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : !processedContentBlocks || processedContentBlocks.length === 0 ? (
                  <Alert
                    severity='warning'
                    sx={{
                      backgroundColor: warning[50],
                      color: warning[700],
                      '& .MuiAlert-icon': {
                        color: warning[700],
                      },
                    }}
                  >
                    {t('dtbs.library.detail.previewWarnings.noContentAvailable')}
                  </Alert>
                ) : (
                  <Box
                    sx={{
                      border: `1px solid ${neutral[200]}`,
                      borderRadius: 2,
                      p: 10,
                    }}
                  >
                    <Stack spacing={6}>
                      {processedContentBlocks.map((block, index) => (
                        <BlockPreview
                          key={`block-${index}`}
                          block={block}
                        />
                      ))}
                    </Stack>
                  </Box>
                )}
                {dtb.contentReferences && dtb.contentReferences.length > 0 && (
                  <PolicyReferencePreview contentReferences={dtb.contentReferences} />
                )}
              </Stack>
            )}

            {currentState === 'quiz' && (
              <Stack spacing={6}>
                {processedQuestions === null ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : !processedQuestions || processedQuestions.length === 0 ? (
                  <Alert
                    severity='warning'
                    sx={{
                      backgroundColor: warning[50],
                      color: warning[700],
                      '& .MuiAlert-icon': {
                        color: warning[700],
                      },
                    }}
                  >
                    {t('dtbs.library.detail.previewWarnings.noQuestionSet')}
                  </Alert>
                ) : (
                  processedQuestions.map((question, index) => (
                    <Stack
                      key={`question-${index}`}
                      spacing={4}
                    >
                      <Typography
                        variant='subtitleSm'
                        component='div'
                      >
                        {parse(question.question)}
                      </Typography>
                      <RadioGroup
                        value={selectedAnswer}
                        onChange={(e) => setSelectedAnswer(e.target.value)}
                      >
                        {question.choices.map((choice) => (
                          <FormControlLabel
                            key={choice.id}
                            value={choice.id}
                            control={<Radio />}
                            label={choice.value}
                          />
                        ))}
                      </RadioGroup>
                    </Stack>
                  ))
                )}
              </Stack>
            )}

            {currentState === 'results' && (
              <Stack spacing={6}>
                {processedQuestions === null ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
                    <CircularProgress />
                  </Box>
                ) : !processedQuestions || processedQuestions.length === 0 ? (
                  <Alert
                    severity='warning'
                    sx={{
                      backgroundColor: warning[50],
                      color: warning[700],
                      '& .MuiAlert-icon': {
                        color: warning[700],
                      },
                    }}
                  >
                    {t('dtbs.library.detail.previewWarnings.noQuestionSet')}
                  </Alert>
                ) : (
                  processedQuestions.map((question, index) => (
                    <Stack
                      key={`question-${index}`}
                      spacing={4}
                    >
                      <Typography
                        variant='subtitleSm'
                        component='div'
                      >
                        {parse(question.question)}
                      </Typography>
                      <RadioGroup value={submittedAnswer}>
                        {question.choices.map((choice) => (
                          <FormControlLabel
                            key={choice.id}
                            value={choice.id}
                            control={<Radio disabled />}
                            label={choice.value}
                          />
                        ))}
                      </RadioGroup>
                      <Stack
                        direction='row'
                        spacing={1}
                        alignItems='center'
                        sx={{
                          color: submittedAnswer === question.correctChoiceId ? success[500] : error[500],
                        }}
                      >
                        {submittedAnswer === question.correctChoiceId ? <Check /> : <Close />}
                        <Typography variant='subtitleMd'>
                          {submittedAnswer === question.correctChoiceId
                            ? t('dtbs.library.detail.previewFeedback.correct')
                            : t('dtbs.library.detail.previewFeedback.incorrect')}
                        </Typography>
                      </Stack>
                    </Stack>
                  ))
                )}
              </Stack>
            )}
          </Box>
        ) : (
          <Typography
            variant='body1'
            color='error'
          >
            No DTB data available for preview.
          </Typography>
        )}
      </Box>
    </Modal>
  );
};

export default DTBPreview;
